import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, MessageSquare, MapPin, Phone, Mail, ArrowLeft, CheckCircle2, Camera, Loader2, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-[#a0a5b5] mb-1.5 block">
      {label} {required && <span className="text-[#00e676]">*</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full bg-[#0a0b0e] border border-[#262a33] rounded-xl px-4 py-3 text-white placeholder:text-[#64697a] outline-none focus:ring-2 focus:ring-[#00e676] focus:border-transparent transition-colors";

export default function FeedbackLanding() {
  const [settings, setSettings] = useState(null);
  const [view, setView] = useState("home"); // home | form | done
  const [submitting, setSubmitting] = useState(false);
  const [hover, setHover] = useState(0);
  const [form, setForm] = useState({
    first_name: "", phone: "", email: "", vehicle: "", service: "",
    subject: "", comment: "", allow_recontact: false, rating: 0,
  });

  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
    if (window.__ccTracked) return;
    window.__ccTracked = true;
    const params = new URLSearchParams(window.location.search);
    const type = params.get("ref") === "qr" ? "scan" : "view";
    api.post("/track", { type }).catch(() => {});
    if (type === "scan") api.post("/track", { type: "view" }).catch(() => {});
  }, []);

  const handlePagesJaunes = () => {
    api.post("/track", { type: "pagesjaunes_click" }).catch(() => {});
    window.open(settings?.pagesjaunes_url, "_blank", "noopener");
  };

  const handleRate = (n) => {
    setForm((f) => ({ ...f, rating: n }));
    setView(n >= 4 ? "positive" : "form");
  };

  const handleGoogle = () => {
    api.post("/track", { type: "google_click" }).catch(() => {});
    window.open(settings?.google_url, "_blank", "noopener");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.phone || !form.comment) {
      toast.error("Merci de renseigner le prénom, le téléphone et le commentaire.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", form);
      setView("done");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  if (!settings)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0b0e]">
        <Loader2 className="w-8 h-8 text-[#00e676] animate-spin" />
      </div>
    );

  return (
    <div className="min-h-screen bg-[#0a0b0e] text-white relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.14] pointer-events-none"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1669882571612-4a9c7822cd4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0b0e]/50 via-[#0a0b0e]/92 to-[#0a0b0e] pointer-events-none" />
      <div
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,230,118,0.14) 0%, rgba(0,230,118,0) 70%)" }}
      />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00e676]/40 to-transparent pointer-events-none" />

      <div className="relative max-w-md mx-auto px-5 py-8 min-h-screen flex flex-col">
        {/* Header */}
        <div className="flex flex-col items-center text-center fade-up">
          <div className="relative">
            <div className="absolute -inset-1.5 rounded-[26px] bg-gradient-to-b from-[#00e676]/40 to-transparent blur-md opacity-70" />
            <div className="relative rounded-2xl bg-[#12151c] border border-white/10 p-3 shadow-[0_18px_50px_-15px_rgba(0,230,118,0.35)]">
              <img
                src={settings.logo_url}
                alt={settings.company_name}
                className="w-28 h-20 object-contain"
                data-testid="company-logo"
              />
            </div>
          </div>
          <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#00e676] font-bold">
            <ShieldCheck className="w-3.5 h-3.5" /> Votre satisfaction compte
          </div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight mt-1.5" data-testid="company-name">
            {settings.company_name}
          </h2>
          <div className="flex items-center gap-1.5 text-[#64697a] text-xs mt-2">
            <MapPin className="w-3.5 h-3.5" /> <span>{settings.address}</span>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {view === "home" && (
            <motion.div
              key="home"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex-1 flex flex-col justify-center gap-8 py-8"
            >
              <div className="text-center">
                <h1 className="font-display text-3xl font-extrabold tracking-tight leading-tight" data-testid="landing-title">
                  {settings.landing_title}
                </h1>
                <p className="text-[#a0a5b5] mt-3 text-sm">{settings.landing_subtitle}</p>
              </div>

              <div className="flex flex-col items-center gap-3">
                <span className="text-sm text-[#64697a]">Touchez une étoile pour évaluer votre expérience</span>
                <div className="flex gap-2" data-testid="rating-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => handleRate(n)}
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      data-testid={`rate-${n}`}
                      className="p-1 transition-transform active:scale-90 hover:scale-110"
                      aria-label={`${n} étoiles`}
                    >
                      <Star
                        className={`w-10 h-10 transition-colors ${
                          (hover || form.rating) >= n
                            ? "fill-[#00e676] text-[#00e676] drop-shadow-[0_0_10px_rgba(0,230,118,0.55)]"
                            : "text-[#363a45]"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3 text-[#64697a] text-xs">
                <div className="flex-1 h-px bg-[#262a33]" /> ou <div className="flex-1 h-px bg-[#262a33]" />
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => setView("form")}
                  data-testid="remarque-btn"
                  className="w-full bg-[#2a2d35] hover:bg-[#363a45] text-white font-semibold text-base rounded-full py-4 px-6 flex items-center justify-center gap-2 transition-colors active:scale-[0.98] border border-[#363a45]"
                >
                  <MessageSquare className="w-5 h-5" />
                  Envoyer une remarque au garage
                </button>
              </div>

              <div className="flex flex-col items-center gap-3 text-xs text-[#64697a]">
                <div className="flex gap-4">
                  <a href={`tel:${settings.phone}`} className="flex items-center gap-1 hover:text-white transition-colors">
                    <Phone className="w-3.5 h-3.5" /> {settings.phone || "Contact"}
                  </a>
                  <a href={`mailto:${settings.email}`} className="flex items-center gap-1 hover:text-white transition-colors">
                    <Mail className="w-3.5 h-3.5" /> {settings.email}
                  </a>
                </div>
                <div className="flex gap-3 text-[#64697a]">
                  <Link to="/confidentialite" className="hover:text-white transition-colors" data-testid="privacy-link">
                    Confidentialité
                  </Link>
                  <span>·</span>
                  <Link to="/mentions-legales" className="hover:text-white transition-colors">
                    Mentions légales
                  </Link>
                </div>
              </div>
            </motion.div>
          )}

          {view === "form" && (
            <motion.form
              key="form"
              onSubmit={submit}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex-1 py-6"
            >
              <button
                type="button"
                onClick={() => setView("home")}
                className="flex items-center gap-1 text-[#a0a5b5] text-sm mb-4 hover:text-white transition-colors"
                data-testid="form-back-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Retour
              </button>
              <h2 className="font-display text-2xl font-bold mb-1">Envoyer une remarque</h2>
              <p className="text-[#64697a] text-sm mb-6">Votre message est privé et adressé directement au garage.</p>

              <div className="flex flex-col gap-4">
                <Field label="Prénom" required>
                  <input className={inputCls} value={form.first_name} onChange={set("first_name")} data-testid="input-first-name" placeholder="Votre prénom" />
                </Field>
                <Field label="Téléphone" required>
                  <input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="input-phone" placeholder="06 XX XX XX XX" />
                </Field>
                <Field label="E-mail (facultatif)">
                  <input className={inputCls} value={form.email} onChange={set("email")} data-testid="input-email" placeholder="vous@exemple.fr" />
                </Field>
                <Field label="Véhicule concerné">
                  <input className={inputCls} value={form.vehicle} onChange={set("vehicle")} data-testid="input-vehicle" placeholder="Ex : Peugeot 308" />
                </Field>
                <Field label="Prestation réalisée">
                  <input className={inputCls} value={form.service} onChange={set("service")} data-testid="input-service" placeholder="Ex : Révision, freins..." />
                </Field>
                <Field label="Objet du message">
                  <input className={inputCls} value={form.subject} onChange={set("subject")} data-testid="input-subject" placeholder="Objet" />
                </Field>
                <Field label="Commentaire" required>
                  <textarea className={inputCls + " min-h-[110px] resize-none"} value={form.comment} onChange={set("comment")} data-testid="input-comment" placeholder="Dites-nous tout..." />
                </Field>

                <label className="flex items-start gap-3 bg-[#161920] border border-[#262a33] rounded-xl p-3 cursor-pointer">
                  <input type="checkbox" checked={form.allow_recontact} onChange={set("allow_recontact")} data-testid="input-recontact" className="mt-0.5 w-5 h-5 accent-[#00e676]" />
                  <span className="text-sm text-[#a0a5b5]">J'autorise CC Auto 30 à me recontacter au sujet de ma remarque.</span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-feedback-btn"
                  className="w-full bg-[#00e676] hover:bg-[#00c853] text-[#04120a] font-bold rounded-full py-4 flex items-center justify-center gap-2 transition-colors active:scale-[0.98] disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Envoyer ma remarque"}
                </button>
              </div>
            </motion.form>
          )}

          {view === "positive" && (
            <motion.div
              key="positive"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex-1 flex flex-col justify-center gap-6 py-8 text-center"
            >
              <div className="flex justify-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`w-8 h-8 ${i < form.rating ? "fill-[#00e676] text-[#00e676]" : "text-[#363a45]"}`} />
                ))}
              </div>
              <div>
                <h1 className="font-display text-2xl font-extrabold tracking-tight">Merci beaucoup ! 🙌</h1>
                <p className="text-[#a0a5b5] mt-2 text-sm">
                  Nous sommes ravis que votre expérience ait été positive. Partagez-la en quelques secondes sur PagesJaunes, cela nous aide énormément.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handlePagesJaunes}
                  data-testid="positive-pagesjaunes-btn"
                  className="w-full bg-[#00e676] hover:bg-[#00c853] text-[#04120a] font-bold text-base rounded-full py-4 px-6 flex items-center justify-center gap-2 transition-colors active:scale-[0.98] shadow-[0_12px_35px_-8px_rgba(0,230,118,0.6)]"
                >
                  <Star className="w-5 h-5 fill-current" />
                  Donner mon avis sur PagesJaunes
                </button>
                {settings.google_url ? (
                  <button
                    onClick={handleGoogle}
                    data-testid="positive-google-btn"
                    className="w-full bg-white hover:bg-[#f1f3f4] text-[#1a1a1a] font-bold text-base rounded-full py-4 px-6 flex items-center justify-center gap-2 transition-colors active:scale-[0.98]"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    </svg>
                    Donner mon avis sur Google
                  </button>
                ) : null}
                <button
                  onClick={() => setView("form")}
                  data-testid="positive-to-form-btn"
                  className="text-sm text-[#a0a5b5] hover:text-white transition-colors"
                >
                  Envoyer plutôt une remarque privée au garage
                </button>
              </div>
            </motion.div>
          )}

          {view === "done" && (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex-1 flex flex-col items-center justify-center text-center gap-4 py-10"
            >
              <div className="w-20 h-20 rounded-full bg-[#00e676]/10 flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-[#00e676]" />
              </div>
              <h2 className="font-display text-2xl font-bold" data-testid="thank-you-title">Merci pour votre retour.</h2>
              <p className="text-[#a0a5b5] max-w-xs">L'équipe CC Auto 30 reviendra vers vous rapidement.</p>
              <button
                onClick={() => { setView("home"); setForm({ first_name: "", phone: "", email: "", vehicle: "", service: "", subject: "", comment: "", allow_recontact: false, rating: 0 }); }}
                className="mt-2 text-[#00e676] font-semibold text-sm hover:underline"
                data-testid="back-home-btn"
              >
                Retour à l'accueil
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
